import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ReactNode, Suspense, lazy, useMemo } from "react";
import { isStandaloneDisplay } from "@/lib/standalone";

// Installed-app visitors get the registration-first onboarding version of
// the existing Auth page.
const AppOnboarding = lazy(() => import("@/pages/Auth"));

export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const standalone = useMemo(() => isStandaloneDisplay(), []);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-gradient-primary animate-glow-pulse" />
      </div>
    );
  }
  if (!user) {
    // Installed Android app / TWA / standalone PWA: registration-first
    // onboarding. Normal browser tabs keep the existing behaviour.
    if (standalone) {
      return (
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-gradient-primary animate-glow-pulse" />
            </div>
          }
        >
          <AppOnboarding onboarding />
        </Suspense>
      );
    }
    // Preserve where the visitor wanted to go so /auth returns them there.
    const intended = `${location.pathname}${location.search}`;
    const next = intended && intended !== "/app/dashboard" ? `?next=${encodeURIComponent(intended)}` : "";
    return <Navigate to={`/auth${next}`} replace />;
  }
  return <>{children}</>;
}
